'use client';

import { searchJiraEpicBySummary } from '@/domain/jira/api';
import {
  useCreateJiraEpic,
  useJiraComponents,
  useJiraConnect,
  useJiraStatus,
  useJiraTeams,
  useJiraVersions,
} from '@/domain/jira/hooks';
import { type JiraComponent, type JiraTeam, type JiraVersion } from '@/domain/jira/types';
import { type RoadMapUpdate } from '@/domain/roadmap/types';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { FormControl } from '@andritzot/metris-web-ui/inputs/form-control';
import { InputLabel } from '@andritzot/metris-web-ui/inputs/input-label';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Grid } from '@andritzot/metris-web-ui/layout/grid';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import { InfoOutlined } from '@mui/icons-material';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { type Control, Controller, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import { JiraEpicConflictDialog } from '../../JiraEpicConflictDialog';
import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

export const JIRA_BASE_URL = 'https://andritz.atlassian.net/browse';

export interface JiraAutomationHandle {
  createEpicIfCloneMode: (summary: string, description: string, roadmapId: number) => Promise<string | undefined>;
  getSettings: () => { shouldCreateEpic: boolean; teamId: string; fixVersionId: string; componentId: string };
}

interface JiraAutomationSectionProps {
  control: Control<RoadMapUpdate>;
  viewOnly: boolean;
}

export const JiraAutomationSection = forwardRef<JiraAutomationHandle, JiraAutomationSectionProps>(
  function JiraAutomationSection({ control, viewOnly }, ref) {
    const [polling, setPolling] = useState(false);
    const [teamId, setTeamId] = useState('');
    const [fixVersionId, setFixVersionId] = useState('');
    const [componentId, setComponentId] = useState('');
    const [connectAs, setConnectAs] = useState<'clone' | 'link'>('link');
    const [conflictState, setConflictState] = useState<{
      existingUrl: string;
      resolve: (choice: 'clone' | 'skip') => void;
    } | null>(null);

    const currentJiraTicket = useWatch({ control, name: 'jiraTicket' });

    const { data: status } = useJiraStatus(polling);
    const isConnected = status?.connected ?? false;

    const { mutateAsync: getConnectUrl, isPending: isConnecting } = useJiraConnect();
    const { mutateAsync: createEpic } = useCreateJiraEpic();
    const { data: versions = [], isLoading: versionsLoading } = useJiraVersions(isConnected);
    const { data: teams = [], isLoading: teamsLoading } = useJiraTeams(isConnected);
    const { data: components = [], isLoading: componentsLoading } = useJiraComponents(isConnected);

    const askConflictResolution = (existingUrl: string) =>
      new Promise<'clone' | 'skip'>((resolve) => {
        setConflictState({ existingUrl, resolve });
      });

    const resetCloneFields = () => {
      setTeamId('');
      setFixVersionId('');
      setComponentId('');
      setConnectAs('link');
    };

    const handleConnect = async () => {
      try {
        const url = await getConnectUrl();
        const popup = globalThis.open(url, '_blank', 'width=600,height=700,noopener,noreferrer');
        if (popup) popup.opener = null;
        setPolling(true);
      } catch {
        toast.error('Failed to initiate Jira connection');
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        createEpicIfCloneMode: async (summary, description, roadmapId) => {
          if (connectAs !== 'clone' || !isConnected) return undefined;
          const existing = await searchJiraEpicBySummary(summary);
          if (existing) {
            const choice = await askConflictResolution(existing.url);
            setConflictState(null);
            if (choice === 'skip') {
              resetCloneFields();
              return currentJiraTicket ? undefined : existing.url;
            }
          }
          const result = await createEpic({ summary, description, roadmapId, teamId, fixVersionId, componentId });
          const ticket = `${JIRA_BASE_URL}/${result.key}`;
          setConnectAs('link');
          return ticket;
        },
        getSettings: () => ({
          shouldCreateEpic: connectAs === 'clone' && isConnected,
          teamId,
          fixVersionId,
          componentId,
        }),
      }),
      [connectAs, isConnected, createEpic, currentJiraTicket, teamId, fixVersionId, componentId],
    );

    return (
      <Box>
        <Divider sx={{ mb: 2, borderColor: 'divider' }} />
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          <Grid size="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Jira Automation
              </Typography>
              <Tooltip title="When prompted, select 'andritz.atlassian.net'">
                <InfoOutlined sx={{ color: 'primary.main', fontSize: 18 }} />
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="connect-as-label">Connect As</InputLabel>
              <Select
                labelId="connect-as-label"
                label="Connect As"
                value={connectAs}
                disabled={viewOnly}
                onChange={(e) => setConnectAs(e.target.value as 'clone' | 'link')}
              >
                <MenuItem value="clone">Clone</MenuItem>
                <MenuItem value="link">Link</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {connectAs === 'clone' && (
            <>
              <Grid size="auto">
                <Button
                  variant="outlined"
                  disabled={isConnected || viewOnly}
                  loading={isConnecting}
                  onClick={handleConnect}
                >
                  {isConnected ? 'Connected' : 'Connect'}
                </Button>
              </Grid>
              <Grid size="grow">
                <Autocomplete<JiraTeam, false, false, false>
                  options={teams}
                  value={teams.find((t) => t.id === teamId) ?? null}
                  onChange={(_, selected) => setTeamId(selected?.id ?? '')}
                  getOptionLabel={(opt) => opt.displayName}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  label="Jira Team"
                  placeholder="Search team..."
                  size="small"
                  fullWidth
                  loading={teamsLoading}
                  disabled={!isConnected || viewOnly}
                />
              </Grid>
              <Grid size="grow">
                <Autocomplete<JiraVersion, false, false, false>
                  options={versions}
                  value={versions.find((v) => v.id === fixVersionId) ?? null}
                  onChange={(_, selected) => setFixVersionId(selected?.id ?? '')}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  label="Jira Fix Version"
                  placeholder="Search version..."
                  size="small"
                  fullWidth
                  loading={versionsLoading}
                  disabled={!isConnected || viewOnly}
                />
              </Grid>
              <Grid size="grow">
                <Autocomplete<JiraComponent, false, false, false>
                  options={components}
                  value={components.find((c) => c.id === componentId) ?? null}
                  onChange={(_, selected) => setComponentId(selected?.id ?? '')}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  label="Jira Component"
                  placeholder="Search component..."
                  size="small"
                  fullWidth
                  loading={componentsLoading}
                  disabled={!isConnected || viewOnly}
                />
              </Grid>
            </>
          )}
          {connectAs === 'link' && (
            <Grid size="grow">
              <Controller
                name="jiraTicket"
                control={control}
                render={({ field }) => (
                  <FieldActionsWrapper copyValue={field.value ?? ''}>
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Jira Ticket URL"
                      placeholder="https://andritz.atlassian.net/browse/OPP-..."
                      size="small"
                      fullWidth
                      disabled={viewOnly}
                    />
                  </FieldActionsWrapper>
                )}
              />
            </Grid>
          )}
        </Grid>
        <Divider sx={{ mt: 2, borderColor: 'divider' }} />
        {conflictState && (
          <JiraEpicConflictDialog
            open
            existingEpicUrl={conflictState.existingUrl}
            onCloneAnyway={() => conflictState.resolve('clone')}
            onSkip={() => conflictState.resolve('skip')}
          />
        )}
      </Box>
    );
  },
);
